import { useMountEffect } from "@/features/smile-simulation/hooks/useMountEffect";
import { JustScanLogo } from "@repo/core/assets";
import { useClinicCompatibleAuth } from "@repo/core/hooks/useClinicCompatibleAuth";
import { normalizeClinicClientKrLoginIdentifier } from "@repo/core/services/auth/clinicClientKr";
import { auth as firebaseAuth } from "@repo/core/services/firebase";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";

import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";

import { callSmileSimulationAPI } from "../api/smileSimulation.api";
import {
  getSmileSimulationDefaultLanguage,
  updateSmileSimulationDefaultLanguage,
} from "../api/smileSimulation.language";
import {
  getSmileSimulationSignedReadUrl,
  getSmileSimulationWatermarkSignedUrls,
  uploadSmileSimulationClinicWatermark,
} from "../api/smileSimulation.storage";
import {
  createSmileSimulationExperienceImageDownloadFileNameBase,
  createSmileSimulationImageDownloadFileNameBase,
  createSmileSimulationTrimmedWatermarkBlob,
  createSmileSimulationWatermarkedImageBlob,
  downloadSmileSimulationImage,
  downloadSmileSimulationImageBlob,
} from "../lib/image";
import { getSmileSimulationProgressLabel } from "../lib/progress";
import {
  createSmileSimulationPatientId,
  createSmileSimulationPatientViewModel,
  groupSmileSimulationRecordsByPatient,
} from "../lib/view-model";
import { getSmileSimulationCopy } from "../model/i18n";
import { smileSimulationReducer } from "../model/reducer";
import {
  selectFooterMessage,
  selectHasComparisonResult,
  selectIsCaptureDisabled,
  selectIsSimulationProcessing,
  selectSimulationProgressOffset,
} from "../model/selectors";
import { initialSmileSimulationState } from "../model/state";
import type {
  SmileSimulationLanguage,
  SmileSimulationPromptVersion,
  SmileSimulationStep,
} from "../model/types";
import { useSmileSimulationCamera } from "./useSmileSimulationCamera";
import { useSmileSimulationComparison } from "./useSmileSimulationComparison";
import { useSmileSimulationProgress } from "./useSmileSimulationProgress";
import { useSmileSimulationRecords } from "./useSmileSimulationRecords";
import { useSmileSimulationViewport } from "./useSmileSimulationViewport";

const EXPERIENCE_SESSION_CODE_PREFIX = "experience";

type SmileSimulationWatermarkAssets = {
  clinicWatermarkUrl: string | null;
  serviceWatermarkUrl: string | null;
};

type SmileSimulationDownloadPreviewState = {
  clinicWatermarkUrl: string | null;
  errorMessage: string | null;
  fileNameBase: string;
  hasClinicWatermark: boolean;
  includeClinicWatermark: boolean;
  includeServiceWatermark: boolean;
  isPreparingPreview: boolean;
  isUploadingClinicWatermark: boolean;
  previewBlob: Blob | null;
  previewImageUrl: string | null;
  serviceWatermarkUrl: string | null;
  sourceImageUrl: string;
};

type UseSmileSimulationControllerOptions = {
  allowUnauthenticated?: boolean;
};

export function useSmileSimulationController({
  allowUnauthenticated = false,
}: UseSmileSimulationControllerOptions = {}) {
  const authState = useClinicCompatibleAuth();
  const [state, dispatch] = useReducer(
    smileSimulationReducer,
    initialSmileSimulationState,
  );
  const [isDownloadingImage, setIsDownloadingImage] = useState(false);
  const [downloadPreview, setDownloadPreview] =
    useState<SmileSimulationDownloadPreviewState | null>(null);
  const copy = useMemo(
    () =>
      getSmileSimulationCopy(
        allowUnauthenticated ? "ko" : state.settings.language,
      ),
    [allowUnauthenticated, state.settings.language],
  );
  const clinicId = allowUnauthenticated
    ? null
    : (authState.rdsCompanyId?.toString() ?? null);
  const {
    errorMessage: recordsErrorMessage,
    refresh,
    records,
  } = useSmileSimulationRecords(clinicId);
  const simulationRequestIdRef = useRef(0);
  const languageRequestIdRef = useRef(0);
  const autoStartedSimulationImageRef = useRef<string | null>(null);
  const hasAutoStartedExperienceRef = useRef(false);
  const resultImageObjectUrlRef = useRef<string | null>(null);
  const downloadPreviewObjectUrlRef = useRef<string | null>(null);
  const downloadPreviewRequestIdRef = useRef(0);
  const sortedRecords = records;
  const patientSummaries = useMemo(
    () => groupSmileSimulationRecordsByPatient(sortedRecords),
    [sortedRecords],
  );
  const currentPatient =
    patientSummaries.find((patient) => patient.id === state.currentPatientId) ??
    (state.currentPatientId &&
    state.patientDraft.name.trim() &&
    state.patientDraft.chartNumber.trim()
      ? createSmileSimulationPatientViewModel({
          id: state.currentPatientId,
          name: state.patientDraft.name.trim(),
          chartNumber: state.patientDraft.chartNumber.trim(),
        })
      : null);

  const setStep = useCallback((step: typeof state.step) => {
    dispatch({ type: "step/set", step });
  }, []);

  useEffect(() => {
    if (allowUnauthenticated || !clinicId) {
      return;
    }

    const requestId = languageRequestIdRef.current + 1;
    languageRequestIdRef.current = requestId;
    let isCancelled = false;

    void getSmileSimulationDefaultLanguage(clinicId).then((language) => {
      if (isCancelled || languageRequestIdRef.current !== requestId) {
        return;
      }

      dispatch({
        type: "settings/languageUpdated",
        language,
      });
    });

    return () => {
      isCancelled = true;
    };
  }, [allowUnauthenticated, clinicId]);

  const setCameraFrameSize = useCallback(
    (frameSize: typeof state.camera.frameSize) => {
      if (!frameSize) {
        return;
      }

      dispatch({ type: "camera/frameSizeUpdated", frameSize });
    },
    [],
  );

  const setCameraRequested = useCallback((message: string) => {
    dispatch({
      type: "camera/requested",
      message,
    });
  }, []);

  const setCameraReady = useCallback(() => {
    dispatch({ type: "camera/ready" });
  }, []);

  const setCameraFailed = useCallback((message: string) => {
    dispatch({
      type: "camera/failed",
      message,
    });
  }, []);

  const setSimulationProgress = useCallback((progress: number) => {
    dispatch({
      type: "simulation/progressUpdated",
      progress,
    });
  }, []);

  const updateEmail = useCallback((email: string) => {
    dispatch({
      type: "auth/emailUpdated",
      email,
    });
  }, []);

  const setEmailError = useCallback((message: string | null) => {
    dispatch({
      type: "auth/emailErrorUpdated",
      message,
    });
  }, []);

  const updatePassword = useCallback((password: string) => {
    dispatch({
      type: "auth/passwordUpdated",
      password,
    });
  }, []);

  const setPasswordError = useCallback((message: string | null) => {
    dispatch({
      type: "auth/passwordErrorUpdated",
      message,
    });
  }, []);

  const setSubmitError = useCallback((message: string | null) => {
    dispatch({
      type: "auth/submitErrorUpdated",
      message,
    });
  }, []);

  const setIsSubmitting = useCallback((isSubmitting: boolean) => {
    dispatch({
      type: "auth/submittingChanged",
      isSubmitting,
    });
  }, []);

  const updatePatientName = useCallback((name: string) => {
    dispatch({
      type: "patientDraft/nameUpdated",
      name,
    });
  }, []);

  const setPatientNameError = useCallback((message: string | null) => {
    dispatch({
      type: "patientDraft/nameErrorUpdated",
      message,
    });
  }, []);

  const updatePatientChartNumber = useCallback((chartNumber: string) => {
    dispatch({
      type: "patientDraft/chartNumberUpdated",
      chartNumber,
    });
  }, []);

  const setPatientChartNumberError = useCallback((message: string | null) => {
    dispatch({
      type: "patientDraft/chartNumberErrorUpdated",
      message,
    });
  }, []);

  const prefillPatientDraft = useCallback(
    (name: string, chartNumber: string) => {
      dispatch({
        type: "patientDraft/prefilled",
        name,
        chartNumber,
      });
    },
    [],
  );

  const resetPatientDraft = useCallback(() => {
    dispatch({
      type: "patientDraft/reset",
    });
  }, []);

  const setCurrentPatientId = useCallback((patientId: string | null) => {
    dispatch({
      type: "session/currentPatientUpdated",
      patientId,
    });
  }, []);

  const updateHistoryQuery = useCallback((query: string) => {
    dispatch({
      type: "history/queryUpdated",
      query,
    });
  }, []);

  const updateSimulationPromptVersion = useCallback(
    (promptVersion: SmileSimulationPromptVersion) => {
      dispatch({
        type: "settings/promptVersionUpdated",
        promptVersion,
      });
    },
    [],
  );

  const updateSimulationLanguage = useCallback(
    (language: SmileSimulationLanguage) => {
      languageRequestIdRef.current += 1;
      dispatch({
        type: "settings/languageUpdated",
        language,
      });

      if (!allowUnauthenticated && clinicId) {
        void updateSmileSimulationDefaultLanguage(clinicId, language).catch(
          (error) => {
            console.error(
              "Failed to update smile simulation default language:",
              error,
            );
          },
        );
      }
    },
    [allowUnauthenticated, clinicId],
  );

  const setSelectedPatientId = useCallback((patientId: string | null) => {
    dispatch({
      type: "history/patientSelected",
      patientId,
    });
  }, []);

  const setSelectedRecordId = useCallback((recordId: string | null) => {
    dispatch({
      type: "history/recordSelected",
      recordId,
    });
  }, []);

  const setComparisonValue = useCallback((value: number) => {
    dispatch({
      type: "comparison/valueUpdated",
      value,
    });
  }, []);

  const setComparisonDragging = useCallback((isDragging: boolean) => {
    dispatch({
      type: "comparison/draggingChanged",
      isDragging,
    });
  }, []);

  const { finishProgress, startProgress, stopProgress } =
    useSmileSimulationProgress({
      onProgressChange: setSimulationProgress,
    });

  const {
    activeFacingMode,
    canSwitchCamera,
    captureCanvasRef,
    captureImage,
    resetCameraFacingModePreference,
    startCameraStream,
    stopCameraStream,
    switchCamera,
    uploadImage,
    videoRef,
  } = useSmileSimulationCamera({
    onCameraFailed: setCameraFailed,
    onCameraReady: setCameraReady,
    onCameraRequested: setCameraRequested,
  });

  const { cameraFrameViewportRef } = useSmileSimulationViewport({
    onFrameSizeChange: setCameraFrameSize,
  });

  const { bindings, comparisonContainerRef } = useSmileSimulationComparison({
    isDragging: state.comparison.isDragging,
    onDraggingChange: setComparisonDragging,
    onValueChange: setComparisonValue,
  });

  const revokeResultImageObjectUrl = useCallback(() => {
    if (!resultImageObjectUrlRef.current) {
      return;
    }

    URL.revokeObjectURL(resultImageObjectUrlRef.current);
    resultImageObjectUrlRef.current = null;
  }, []);

  const revokeDownloadPreviewObjectUrl = useCallback(() => {
    if (!downloadPreviewObjectUrlRef.current) {
      return;
    }

    URL.revokeObjectURL(downloadPreviewObjectUrlRef.current);
    downloadPreviewObjectUrlRef.current = null;
  }, []);

  const closeDownloadPreview = useCallback(() => {
    downloadPreviewRequestIdRef.current += 1;
    revokeDownloadPreviewObjectUrl();
    setDownloadPreview(null);
  }, [revokeDownloadPreviewObjectUrl]);

  const resolveDownloadPreviewWatermarkAssets =
    useCallback(async (): Promise<SmileSimulationWatermarkAssets> => {
      const { clinicWatermarkUrl, serviceWatermarkUrl } =
        await getSmileSimulationWatermarkSignedUrls(clinicId);

      return {
        clinicWatermarkUrl,
        serviceWatermarkUrl,
      };
    }, [clinicId]);

  const createWatermarkedDownloadBlob = useCallback(
    async ({
      clinicWatermarkUrl,
      includeClinicWatermark,
      includeServiceWatermark,
      serviceWatermarkUrl,
      sourceImageUrl,
    }: {
      clinicWatermarkUrl: string | null;
      includeClinicWatermark: boolean;
      includeServiceWatermark: boolean;
      serviceWatermarkUrl: string | null;
      sourceImageUrl: string;
    }) => {
      if (includeClinicWatermark && !clinicWatermarkUrl) {
        throw new Error(
          "Smile simulation clinic watermark asset is unavailable",
        );
      }

      if (includeServiceWatermark && !serviceWatermarkUrl) {
        throw new Error(
          "Smile simulation service watermark asset is unavailable",
        );
      }

      return createSmileSimulationWatermarkedImageBlob({
        clinicWatermarkUrl: includeClinicWatermark ? clinicWatermarkUrl : null,
        imageUrl: sourceImageUrl,
        serviceWatermarkUrl: includeServiceWatermark
          ? serviceWatermarkUrl
          : null,
      });
    },
    [],
  );

  const prepareDownloadPreview = useCallback(
    async ({
      includeClinicWatermark,
      includeServiceWatermark,
      requestId,
      sourceImageUrl,
      watermarkAssets,
    }: {
      includeClinicWatermark: boolean;
      includeServiceWatermark: boolean;
      requestId: number;
      sourceImageUrl: string;
      watermarkAssets?: SmileSimulationWatermarkAssets;
    }) => {
      revokeDownloadPreviewObjectUrl();

      try {
        const resolvedWatermarkAssets =
          watermarkAssets ?? (await resolveDownloadPreviewWatermarkAssets());

        if (downloadPreviewRequestIdRef.current !== requestId) {
          return;
        }

        const nextIncludeClinicWatermark =
          includeClinicWatermark &&
          !!resolvedWatermarkAssets.clinicWatermarkUrl;
        const nextIncludeServiceWatermark =
          includeServiceWatermark &&
          !!resolvedWatermarkAssets.serviceWatermarkUrl;

        if (!nextIncludeClinicWatermark && !nextIncludeServiceWatermark) {
          setDownloadPreview((currentPreview) =>
            currentPreview
              ? {
                  ...currentPreview,
                  clinicWatermarkUrl:
                    resolvedWatermarkAssets.clinicWatermarkUrl,
                  errorMessage: null,
                  hasClinicWatermark:
                    resolvedWatermarkAssets.clinicWatermarkUrl != null,
                  includeClinicWatermark: false,
                  includeServiceWatermark: false,
                  isPreparingPreview: false,
                  isUploadingClinicWatermark: false,
                  previewBlob: null,
                  previewImageUrl: sourceImageUrl,
                  serviceWatermarkUrl:
                    resolvedWatermarkAssets.serviceWatermarkUrl,
                }
              : currentPreview,
          );
          return;
        }

        const previewBlob = await createWatermarkedDownloadBlob({
          clinicWatermarkUrl: resolvedWatermarkAssets.clinicWatermarkUrl,
          includeClinicWatermark: nextIncludeClinicWatermark,
          includeServiceWatermark: nextIncludeServiceWatermark,
          serviceWatermarkUrl: resolvedWatermarkAssets.serviceWatermarkUrl,
          sourceImageUrl,
        });

        if (downloadPreviewRequestIdRef.current !== requestId) {
          return;
        }

        const previewImageUrl = URL.createObjectURL(previewBlob);
        downloadPreviewObjectUrlRef.current = previewImageUrl;

        setDownloadPreview((currentPreview) =>
          currentPreview
            ? {
                ...currentPreview,
                clinicWatermarkUrl: resolvedWatermarkAssets.clinicWatermarkUrl,
                errorMessage: null,
                hasClinicWatermark:
                  resolvedWatermarkAssets.clinicWatermarkUrl != null,
                includeClinicWatermark: nextIncludeClinicWatermark,
                includeServiceWatermark: nextIncludeServiceWatermark,
                isPreparingPreview: false,
                isUploadingClinicWatermark: false,
                previewBlob,
                previewImageUrl,
                serviceWatermarkUrl:
                  resolvedWatermarkAssets.serviceWatermarkUrl,
              }
            : currentPreview,
        );
      } catch (error) {
        if (downloadPreviewRequestIdRef.current !== requestId) {
          return;
        }

        console.error(
          "Failed to prepare smile simulation watermark preview:",
          error,
        );
        setDownloadPreview((currentPreview) =>
          currentPreview
            ? {
                ...currentPreview,
                errorMessage: copy.messages.watermarkPreviewFailure,
                isPreparingPreview: false,
                isUploadingClinicWatermark: false,
                previewBlob: null,
                previewImageUrl: sourceImageUrl,
              }
            : currentPreview,
        );
      }
    },
    [
      createWatermarkedDownloadBlob,
      copy.messages.watermarkPreviewFailure,
      resolveDownloadPreviewWatermarkAssets,
      revokeDownloadPreviewObjectUrl,
    ],
  );

  const openDownloadPreview = useCallback(
    ({
      chartNumber,
      createdAt,
      imageUrl,
      patientName,
    }: {
      chartNumber: string;
      createdAt: Date | string;
      imageUrl: string;
      patientName: string;
    }) => {
      const requestId = downloadPreviewRequestIdRef.current + 1;
      downloadPreviewRequestIdRef.current = requestId;
      revokeDownloadPreviewObjectUrl();

      setDownloadPreview({
        clinicWatermarkUrl: null,
        errorMessage: null,
        fileNameBase: createSmileSimulationImageDownloadFileNameBase({
          chartNumber,
          createdAt,
          patientName,
        }),
        hasClinicWatermark: false,
        includeClinicWatermark: false,
        includeServiceWatermark: true,
        isPreparingPreview: true,
        isUploadingClinicWatermark: false,
        previewBlob: null,
        previewImageUrl: null,
        serviceWatermarkUrl: null,
        sourceImageUrl: imageUrl,
      });

      void (async () => {
        try {
          const watermarkAssets = await resolveDownloadPreviewWatermarkAssets();

          if (downloadPreviewRequestIdRef.current !== requestId) {
            return;
          }

          void prepareDownloadPreview({
            includeClinicWatermark: watermarkAssets.clinicWatermarkUrl != null,
            includeServiceWatermark:
              watermarkAssets.serviceWatermarkUrl != null,
            requestId,
            sourceImageUrl: imageUrl,
            watermarkAssets,
          });
        } catch (error) {
          if (downloadPreviewRequestIdRef.current !== requestId) {
            return;
          }

          console.error(
            "Failed to resolve smile simulation watermark assets:",
            error,
          );
          setDownloadPreview((currentPreview) =>
            currentPreview
              ? {
                  ...currentPreview,
                  errorMessage: null,
                  includeClinicWatermark: false,
                  includeServiceWatermark: false,
                  isPreparingPreview: false,
                  previewBlob: null,
                  previewImageUrl: imageUrl,
                }
              : currentPreview,
          );
        }
      })();
    },
    [
      prepareDownloadPreview,
      resolveDownloadPreviewWatermarkAssets,
      revokeDownloadPreviewObjectUrl,
    ],
  );

  const resetSimulationSession = useCallback(() => {
    revokeResultImageObjectUrl();
    stopProgress({ reset: true });
    dispatch({ type: "simulation/reset" });
  }, [revokeResultImageObjectUrl, stopProgress]);

  const clearCaptureImage = useCallback(() => {
    dispatch({
      type: "capture/setImage",
      image: null,
    });
  }, []);

  const resetCurrentWorkflow = useCallback(() => {
    simulationRequestIdRef.current += 1;
    autoStartedSimulationImageRef.current = null;
    closeDownloadPreview();
    clearCaptureImage();
    resetSimulationSession();
    setSelectedRecordId(null);
  }, [
    clearCaptureImage,
    closeDownloadPreview,
    resetSimulationSession,
    setSelectedRecordId,
  ]);

  useMountEffect(() => {
    return () => {
      simulationRequestIdRef.current += 1;
      downloadPreviewRequestIdRef.current += 1;
      revokeDownloadPreviewObjectUrl();
      revokeResultImageObjectUrl();
    };
  });

  const runSmileSimulation = useCallback(
    async (imageDataUrl: string) => {
      if ((!allowUnauthenticated && !clinicId) || !currentPatient) {
        dispatch({
          type: "simulation/failed",
          message: copy.messages.clinicIdRequired,
        });
        return;
      }

      const currentAuthUser = authState.user ?? firebaseAuth.currentUser;

      if (!allowUnauthenticated && !currentAuthUser) {
        dispatch({
          type: "simulation/failed",
          message: copy.messages.simulationAuthRequired,
        });
        return;
      }

      const previousLatestRecordCreatedAt =
        sortedRecords.find((record) => record.patientId === currentPatient.id)
          ?.createdAt ?? null;
      const requestId = simulationRequestIdRef.current + 1;
      simulationRequestIdRef.current = requestId;

      resetSimulationSession();
      dispatch({
        type: "simulation/requested",
        message: copy.messages.simulationRequest,
      });
      startProgress(requestId * 997 + Date.now());

      try {
        const authToken =
          !allowUnauthenticated && currentAuthUser
            ? await currentAuthUser.getIdToken()
            : undefined;
        const result = await callSmileSimulationAPI({
          authToken,
          chartNumber: allowUnauthenticated
            ? undefined
            : currentPatient.chartNumber,
          eventMode: allowUnauthenticated ? "experience" : undefined,
          imageDataUrl,
          patientName: allowUnauthenticated ? undefined : currentPatient.name,
          promptVersion: state.settings.promptVersion,
        });
        const resultImage = result.image;

        if (simulationRequestIdRef.current !== requestId) {
          return;
        }

        await finishProgress();

        if (simulationRequestIdRef.current !== requestId) {
          return;
        }

        const resultImageUrl = URL.createObjectURL(resultImage);
        revokeResultImageObjectUrl();
        resultImageObjectUrlRef.current = resultImageUrl;
        setSelectedPatientId(currentPatient.id);
        setSelectedRecordId(result.simulationId);

        dispatch({
          type: "simulation/succeeded",
          image: resultImageUrl,
          message: copy.messages.simulationSuccess,
          recordId: result.simulationId,
        });

        void refresh()
          .then((nextRecords) => {
            if (simulationRequestIdRef.current !== requestId) {
              return;
            }

            const matchedRecord =
              nextRecords.find((record) => {
                if (record.patientId !== currentPatient.id) {
                  return false;
                }

                if (!previousLatestRecordCreatedAt) {
                  return true;
                }

                return (
                  new Date(record.createdAt).getTime() >
                  new Date(previousLatestRecordCreatedAt).getTime()
                );
              }) ?? null;

            if (!matchedRecord) {
              return;
            }

            setSelectedPatientId(matchedRecord.patientId);
            setSelectedRecordId(matchedRecord.id);
          })
          .catch(() => undefined);
      } catch (error) {
        if (simulationRequestIdRef.current !== requestId) {
          return;
        }

        stopProgress();
        console.error("Smile simulation request failed:", error);
        dispatch({
          type: "simulation/failed",
          message: copy.messages.simulationFailure,
        });
      }
    },
    [
      clinicId,
      allowUnauthenticated,
      copy.messages.clinicIdRequired,
      copy.messages.simulationAuthRequired,
      copy.messages.simulationFailure,
      copy.messages.simulationRequest,
      copy.messages.simulationSuccess,
      finishProgress,
      authState.user,
      currentPatient,
      refresh,
      resetSimulationSession,
      revokeResultImageObjectUrl,
      setSelectedPatientId,
      setSelectedRecordId,
      sortedRecords,
      startProgress,
      state.settings.promptVersion,
      stopProgress,
    ],
  );

  const handleSubmitLogin = useCallback(async () => {
    const nextEmail = state.auth.email.trim();
    const nextPassword = state.auth.password;
    let hasError = false;

    if (!nextEmail) {
      setEmailError(copy.messages.emailRequired);
      hasError = true;
    } else {
      setEmailError(null);
    }

    if (!nextPassword) {
      setPasswordError(copy.messages.passwordRequired);
      hasError = true;
    } else {
      setPasswordError(null);
    }

    if (hasError) {
      return;
    }

    updateEmail(nextEmail);
    updatePassword(nextPassword);
    setSubmitError(null);
    setIsSubmitting(true);

    try {
      await signInWithEmailAndPassword(
        firebaseAuth,
        normalizeClinicClientKrLoginIdentifier(nextEmail),
        nextPassword,
      );
    } catch (error) {
      console.error("Smile simulation sign in failed:", error);
      setSubmitError(copy.messages.authFailed);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    setIsSubmitting,
    setEmailError,
    setPasswordError,
    setSubmitError,
    copy.messages.authFailed,
    copy.messages.emailRequired,
    copy.messages.passwordRequired,
    state.auth.email,
    state.auth.password,
    updateEmail,
    updatePassword,
  ]);

  const handleLogout = useCallback(async () => {
    resetCurrentWorkflow();
    stopCameraStream();
    resetPatientDraft();
    setCurrentPatientId(null);
    setSelectedPatientId(null);
    updateEmail("");
    updatePassword("");
    setSubmitError(null);

    try {
      await signOut(firebaseAuth);
    } catch (error) {
      console.error("Smile simulation sign out failed:", error);
    }
  }, [
    resetCurrentWorkflow,
    resetPatientDraft,
    setCurrentPatientId,
    setSelectedPatientId,
    setSubmitError,
    stopCameraStream,
    updateEmail,
    updatePassword,
  ]);

  const handleGoHome = useCallback(() => {
    setStep("home");
  }, [setStep]);

  const handleOpenHistory = useCallback(() => {
    setStep("history-list");
  }, [setStep]);

  const handleStartNewSimulation = useCallback(
    (patientId?: string | null) => {
      const selectedPatient =
        patientId == null
          ? null
          : (patientSummaries.find((patient) => patient.id === patientId) ??
            null);

      resetCurrentWorkflow();
      resetCameraFacingModePreference();

      if (allowUnauthenticated && !selectedPatient) {
        const sessionCode = `${EXPERIENCE_SESSION_CODE_PREFIX}-${Date.now()}`;
        const sessionId = createSmileSimulationPatientId(sessionCode);

        prefillPatientDraft(sessionCode, sessionCode);
        setCurrentPatientId(sessionId);
        setSelectedPatientId(sessionId);
        setSelectedRecordId(null);
        setStep("capture");
        return;
      }

      if (selectedPatient) {
        prefillPatientDraft(selectedPatient.name, selectedPatient.chartNumber);
        setCurrentPatientId(selectedPatient.id);
        setSelectedPatientId(selectedPatient.id);
      } else {
        resetPatientDraft();
        setCurrentPatientId(null);
        setSelectedPatientId(null);
      }

      setStep("patient-intake");
    },
    [
      allowUnauthenticated,
      prefillPatientDraft,
      resetCurrentWorkflow,
      resetCameraFacingModePreference,
      resetPatientDraft,
      setCurrentPatientId,
      setSelectedPatientId,
      setSelectedRecordId,
      setStep,
      patientSummaries,
    ],
  );

  const handleSubmitPatientInfo = useCallback(() => {
    const nextName = state.patientDraft.name.trim();
    const nextChartNumber = state.patientDraft.chartNumber.trim();
    let hasError = false;

    if (!nextName) {
      setPatientNameError(copy.messages.patientNameRequired);
      hasError = true;
    } else {
      setPatientNameError(null);
    }

    if (!nextChartNumber) {
      setPatientChartNumberError(copy.messages.chartNumberRequired);
      hasError = true;
    } else {
      setPatientChartNumberError(null);
    }

    if (hasError) {
      return;
    }

    const existingPatient =
      patientSummaries.find(
        (patient) =>
          patient.chartNumber.trim().toLowerCase() ===
          nextChartNumber.toLowerCase(),
      ) ?? null;

    const nextPatientId =
      existingPatient?.id ?? createSmileSimulationPatientId(nextChartNumber);

    prefillPatientDraft(nextName, nextChartNumber);
    setCurrentPatientId(nextPatientId);
    setSelectedPatientId(nextPatientId);
    setSelectedRecordId(null);
    setStep("capture");
  }, [
    patientSummaries,
    prefillPatientDraft,
    copy.messages.chartNumberRequired,
    copy.messages.patientNameRequired,
    setCurrentPatientId,
    setPatientChartNumberError,
    setPatientNameError,
    setSelectedPatientId,
    setSelectedRecordId,
    setStep,
    state.patientDraft.chartNumber,
    state.patientDraft.name,
  ]);

  const handleEditCurrentPatientInfo = useCallback(() => {
    if (!currentPatient) {
      handleStartNewSimulation();
      return;
    }

    prefillPatientDraft(currentPatient.name, currentPatient.chartNumber);
    setCurrentPatientId(currentPatient.id);
    setStep("patient-intake");
  }, [
    currentPatient,
    handleStartNewSimulation,
    prefillPatientDraft,
    setCurrentPatientId,
    setStep,
  ]);

  const handleCapture = useCallback(() => {
    const nextCapturedImage = captureImage();

    if (!nextCapturedImage) {
      return;
    }

    autoStartedSimulationImageRef.current = null;
    resetSimulationSession();
    dispatch({
      type: "capture/setImage",
      image: nextCapturedImage,
    });
    stopCameraStream();
    setStep("result");
  }, [captureImage, resetSimulationSession, setStep, stopCameraStream]);

  const handleUploadCaptureImage = useCallback(
    async (file: File) => {
      if (!file.type.startsWith("image/")) {
        window.alert(copy.messages.imageUploadType);
        return;
      }

      try {
        const nextCapturedImage = await uploadImage(file);

        if (!nextCapturedImage) {
          window.alert(copy.messages.imageUploadFailure);
          return;
        }

        autoStartedSimulationImageRef.current = null;
        resetSimulationSession();
        dispatch({
          type: "capture/setImage",
          image: nextCapturedImage,
        });
        stopCameraStream();
        setStep("result");
      } catch (error) {
        console.error("Smile simulation image upload failed:", error);
        window.alert(copy.messages.imageUploadFailure);
      }
    },
    [
      copy.messages.imageUploadFailure,
      copy.messages.imageUploadType,
      resetSimulationSession,
      setStep,
      stopCameraStream,
      uploadImage,
    ],
  );

  const handleStartSimulation = useCallback(() => {
    if (!state.capture.image || state.simulation.status !== "idle") {
      return;
    }

    if (autoStartedSimulationImageRef.current === state.capture.image) {
      return;
    }

    autoStartedSimulationImageRef.current = state.capture.image;
    void runSmileSimulation(state.capture.image);
  }, [runSmileSimulation, state.capture.image, state.simulation.status]);

  const handleRetake = useCallback(() => {
    simulationRequestIdRef.current += 1;
    autoStartedSimulationImageRef.current = null;
    clearCaptureImage();
    resetSimulationSession();
    setSelectedRecordId(null);
    setStep("capture");
  }, [clearCaptureImage, resetSimulationSession, setSelectedRecordId, setStep]);

  const handleRetrySimulation = useCallback(() => {
    if (!state.capture.image) {
      return;
    }

    void runSmileSimulation(state.capture.image);
  }, [runSmileSimulation, state.capture.image]);

  const handleDownloadImage = useCallback(
    async ({
      fileNameBase,
      imageBlob,
      imageUrl,
    }: {
      fileNameBase: string;
      imageBlob?: Blob;
      imageUrl: string;
    }) => {
      if (isDownloadingImage) {
        return false;
      }

      setIsDownloadingImage(true);

      try {
        if (imageBlob) {
          await downloadSmileSimulationImageBlob({
            fileNameBase,
            imageBlob,
            imageUrl,
          });
        } else {
          await downloadSmileSimulationImage({
            fileNameBase,
            imageUrl,
          });
        }
        return true;
      } catch (error) {
        console.error("Smile simulation image download failed:", error);
        window.alert(copy.messages.imageDownloadFailure);
        return false;
      } finally {
        setIsDownloadingImage(false);
      }
    },
    [copy.messages.imageDownloadFailure, isDownloadingImage],
  );

  const handleSetDownloadPreviewWatermark = useCallback(
    (watermarkType: "clinic" | "service", isEnabled: boolean) => {
      if (
        !downloadPreview ||
        (watermarkType === "clinic" &&
          isEnabled &&
          !downloadPreview.hasClinicWatermark) ||
        (watermarkType === "clinic"
          ? downloadPreview.includeClinicWatermark
          : downloadPreview.includeServiceWatermark) === isEnabled
      ) {
        return;
      }

      const requestId = downloadPreviewRequestIdRef.current + 1;
      downloadPreviewRequestIdRef.current = requestId;

      const nextIncludeClinicWatermark =
        watermarkType === "clinic"
          ? isEnabled
          : downloadPreview.includeClinicWatermark;
      const nextIncludeServiceWatermark =
        watermarkType === "service"
          ? isEnabled
          : downloadPreview.includeServiceWatermark;
      const shouldPreparePreview =
        nextIncludeClinicWatermark || nextIncludeServiceWatermark;

      setDownloadPreview((currentPreview) =>
        currentPreview
          ? {
              ...currentPreview,
              errorMessage: null,
              includeClinicWatermark: nextIncludeClinicWatermark,
              includeServiceWatermark: nextIncludeServiceWatermark,
              isPreparingPreview: shouldPreparePreview,
              isUploadingClinicWatermark: false,
              previewBlob: null,
              previewImageUrl: shouldPreparePreview
                ? null
                : currentPreview.sourceImageUrl,
            }
          : currentPreview,
      );

      void prepareDownloadPreview({
        includeClinicWatermark: nextIncludeClinicWatermark,
        includeServiceWatermark: nextIncludeServiceWatermark,
        requestId,
        sourceImageUrl: downloadPreview.sourceImageUrl,
      });
    },
    [downloadPreview, prepareDownloadPreview],
  );

  const handleUploadClinicWatermark = useCallback(
    async (file: File) => {
      if (!downloadPreview) {
        return;
      }

      if (!file.type.startsWith("image/")) {
        window.alert(copy.messages.imageUploadType);
        return;
      }

      const currentAuthUser = authState.user ?? firebaseAuth.currentUser;

      if (!clinicId || !currentAuthUser) {
        window.alert(copy.messages.clinicWatermarkUploadUnavailable);
        return;
      }

      const requestId = downloadPreviewRequestIdRef.current + 1;
      downloadPreviewRequestIdRef.current = requestId;

      setDownloadPreview((currentPreview) =>
        currentPreview
          ? {
              ...currentPreview,
              errorMessage: null,
              hasClinicWatermark: true,
              includeClinicWatermark: true,
              isPreparingPreview: true,
              isUploadingClinicWatermark: true,
              previewBlob: null,
              previewImageUrl: null,
            }
          : currentPreview,
      );

      try {
        const trimmedWatermarkBlob =
          await createSmileSimulationTrimmedWatermarkBlob(file);
        const uploadResult =
          await uploadSmileSimulationClinicWatermark(trimmedWatermarkBlob);
        const clinicWatermarkUrl = await getSmileSimulationSignedReadUrl(
          uploadResult.path,
        );
        const serviceWatermarkUrl =
          downloadPreview.serviceWatermarkUrl ??
          (await resolveDownloadPreviewWatermarkAssets()).serviceWatermarkUrl;

        if (downloadPreviewRequestIdRef.current !== requestId) {
          return;
        }

        void prepareDownloadPreview({
          includeClinicWatermark: true,
          includeServiceWatermark: downloadPreview.includeServiceWatermark,
          requestId,
          sourceImageUrl: downloadPreview.sourceImageUrl,
          watermarkAssets: {
            clinicWatermarkUrl,
            serviceWatermarkUrl,
          },
        });
      } catch (error) {
        if (downloadPreviewRequestIdRef.current !== requestId) {
          return;
        }

        console.error(
          "Failed to upload smile simulation clinic watermark:",
          error,
        );
        setDownloadPreview((currentPreview) =>
          currentPreview
            ? {
                ...currentPreview,
                clinicWatermarkUrl: downloadPreview.clinicWatermarkUrl,
                errorMessage: copy.messages.clinicWatermarkUploadFailure,
                hasClinicWatermark: downloadPreview.hasClinicWatermark,
                includeClinicWatermark: downloadPreview.includeClinicWatermark,
                isPreparingPreview: false,
                isUploadingClinicWatermark: false,
                previewImageUrl:
                  currentPreview.previewImageUrl ??
                  currentPreview.sourceImageUrl,
              }
            : currentPreview,
        );
      }
    },
    [
      authState.user,
      clinicId,
      copy.messages.clinicWatermarkUploadFailure,
      copy.messages.clinicWatermarkUploadUnavailable,
      copy.messages.imageUploadType,
      downloadPreview,
      prepareDownloadPreview,
      resolveDownloadPreviewWatermarkAssets,
    ],
  );

  const handleConfirmDownloadPreview = useCallback(async () => {
    if (!downloadPreview || downloadPreview.isPreparingPreview) {
      return;
    }

    try {
      const shouldApplyAnyWatermark =
        downloadPreview.includeClinicWatermark ||
        downloadPreview.includeServiceWatermark;
      const imageBlob =
        shouldApplyAnyWatermark && !downloadPreview.previewBlob
          ? await createWatermarkedDownloadBlob({
              clinicWatermarkUrl: downloadPreview.clinicWatermarkUrl,
              includeClinicWatermark: downloadPreview.includeClinicWatermark,
              includeServiceWatermark: downloadPreview.includeServiceWatermark,
              serviceWatermarkUrl: downloadPreview.serviceWatermarkUrl,
              sourceImageUrl: downloadPreview.sourceImageUrl,
            })
          : downloadPreview.previewBlob;

      const didDownload = await handleDownloadImage({
        fileNameBase: downloadPreview.fileNameBase,
        imageBlob: shouldApplyAnyWatermark
          ? (imageBlob ?? undefined)
          : undefined,
        imageUrl: downloadPreview.sourceImageUrl,
      });

      if (didDownload) {
        closeDownloadPreview();
      }
    } catch (error) {
      console.error(
        "Failed to confirm smile simulation download preview:",
        error,
      );
      window.alert(copy.messages.imageDownloadFailure);
    }
  }, [
    closeDownloadPreview,
    copy.messages.imageDownloadFailure,
    createWatermarkedDownloadBlob,
    downloadPreview,
    handleDownloadImage,
  ]);

  const handleDownloadCurrentResultImage = useCallback(() => {
    if (
      !currentPatient ||
      !state.simulation.resultImage ||
      state.simulation.status !== "completed"
    ) {
      return;
    }

    if (allowUnauthenticated) {
      if (isDownloadingImage) {
        return;
      }

      const imageUrl = state.simulation.resultImage;
      const fileNameBase =
        createSmileSimulationExperienceImageDownloadFileNameBase(new Date());

      setIsDownloadingImage(true);
      void (async () => {
        try {
          const imageBlob = await createSmileSimulationWatermarkedImageBlob({
            clinicWatermarkUrl: null,
            imageUrl,
            serviceWatermarkUrl: JustScanLogo,
          });

          await downloadSmileSimulationImageBlob({
            fileNameBase,
            imageBlob,
            imageUrl,
          });
        } catch (error) {
          console.error(
            "Failed to download experience smile simulation image:",
            error,
          );
          window.alert(copy.messages.imageDownloadFailure);
        } finally {
          setIsDownloadingImage(false);
        }
      })();
      return;
    }

    const selectedCurrentRecord =
      state.history.selectedRecordId == null
        ? null
        : (sortedRecords.find(
            (record) =>
              record.id === state.history.selectedRecordId &&
              record.patientId === currentPatient.id,
          ) ?? null);

    openDownloadPreview({
      chartNumber:
        selectedCurrentRecord?.chartNumber ?? currentPatient.chartNumber,
      createdAt: selectedCurrentRecord?.createdAt ?? new Date(),
      imageUrl: state.simulation.resultImage,
      patientName: selectedCurrentRecord?.patientName ?? currentPatient.name,
    });
  }, [
    allowUnauthenticated,
    copy.messages.imageDownloadFailure,
    currentPatient,
    isDownloadingImage,
    openDownloadPreview,
    sortedRecords,
    state.history.selectedRecordId,
    state.simulation.resultImage,
    state.simulation.status,
  ]);

  const handleDownloadSelectedRecordImage = useCallback(() => {
    if (state.history.selectedRecordId == null) {
      return;
    }

    const selectedRecord =
      sortedRecords.find(
        (record) => record.id === state.history.selectedRecordId,
      ) ?? null;

    if (!selectedRecord) {
      return;
    }

    openDownloadPreview({
      chartNumber: selectedRecord.chartNumber,
      createdAt: selectedRecord.createdAt,
      imageUrl: selectedRecord.resultImage,
      patientName: selectedRecord.patientName,
    });
  }, [openDownloadPreview, sortedRecords, state.history.selectedRecordId]);

  const handleOpenPatientDetail = useCallback(
    (patientId: string) => {
      if (allowUnauthenticated) {
        const selectedPatient =
          patientSummaries.find((patient) => patient.id === patientId) ?? null;
        const latestRecord = selectedPatient?.latestRecord ?? null;

        if (!selectedPatient || !latestRecord) {
          return;
        }

        setSelectedPatientId(selectedPatient.id);
        setSelectedRecordId(latestRecord.id);
        setStep("simulation-detail");
        return;
      }

      setSelectedPatientId(patientId);
      setSelectedRecordId(null);
      setStep("patient-detail");
    },
    [
      allowUnauthenticated,
      patientSummaries,
      setSelectedPatientId,
      setSelectedRecordId,
      setStep,
    ],
  );

  const handleOpenCurrentPatientDetail = useCallback(() => {
    if (!currentPatient) {
      return;
    }

    setSelectedPatientId(currentPatient.id);
    setStep("patient-detail");
  }, [currentPatient, setSelectedPatientId, setStep]);

  const handleOpenRecordDetail = useCallback(
    (recordId: string) => {
      const selectedRecord =
        sortedRecords.find((record) => record.id === recordId) ?? null;

      if (!selectedRecord) {
        setSelectedPatientId(null);
        setSelectedRecordId(recordId);
        setStep("simulation-detail");
        return;
      }

      setSelectedPatientId(selectedRecord.patientId);
      setSelectedRecordId(selectedRecord.id);
      setStep("simulation-detail");
    },
    [setSelectedPatientId, setSelectedRecordId, setStep, sortedRecords],
  );

  const handleBackToHistoryList = useCallback(() => {
    setStep("history-list");
  }, [setStep]);

  const handleBackToPatientDetail = useCallback(() => {
    setStep("patient-detail");
  }, [setStep]);

  const hasClinicAccess = allowUnauthenticated || authState.authenticated;
  const selectedPatient =
    patientSummaries.find(
      (patient) => patient.id === state.history.selectedPatientId,
    ) ?? null;
  const selectedRecord =
    sortedRecords.find(
      (record) => record.id === state.history.selectedRecordId,
    ) ?? null;
  const resolvedStep: SmileSimulationStep = !hasClinicAccess
    ? "login"
    : state.step === "login"
      ? "home"
      : state.step === "capture" && !currentPatient
        ? "patient-intake"
        : state.step === "result" && !currentPatient
          ? "patient-intake"
          : state.step === "result" && !state.capture.image
            ? "capture"
            : state.step === "patient-detail" && !selectedPatient
              ? "history-list"
              : state.step === "simulation-detail" && !selectedRecord
                ? selectedPatient
                  ? "patient-detail"
                  : "history-list"
                : state.step;

  useEffect(() => {
    if (!allowUnauthenticated) {
      return;
    }

    if (resolvedStep !== "home") {
      hasAutoStartedExperienceRef.current = false;
      return;
    }

    if (hasAutoStartedExperienceRef.current) {
      return;
    }

    hasAutoStartedExperienceRef.current = true;
    handleStartNewSimulation();
  }, [allowUnauthenticated, handleStartNewSimulation, resolvedStep]);

  const resolvedState =
    resolvedStep === state.step ? state : { ...state, step: resolvedStep };
  const historyQuery = state.history.query.trim().toLowerCase();
  const filteredPatients = historyQuery
    ? patientSummaries.filter(
        (patient) =>
          patient.name.toLowerCase().includes(historyQuery) ||
          patient.chartNumber.toLowerCase().includes(historyQuery),
      )
    : patientSummaries;
  const selectedPatientSummary =
    patientSummaries.find((patient) => patient.id === selectedPatient?.id) ??
    null;
  const selectedRecordDetail = selectedRecord
    ? {
        ...selectedRecord,
        patient: createSmileSimulationPatientViewModel({
          id: selectedRecord.patientId,
          name: selectedRecord.patientName,
          chartNumber: selectedRecord.chartNumber,
        }),
      }
    : null;
  const recentPatients = patientSummaries.slice(0, 3);
  const todayKey = new Date().toDateString();
  const todayRecordCount = sortedRecords.filter(
    (record) => new Date(record.createdAt).toDateString() === todayKey,
  ).length;

  const view = {
    footerMessage:
      resolvedState.simulation.status === "processing"
        ? getSmileSimulationProgressLabel(
            resolvedState.simulation.progress,
            copy.progress,
          )
        : selectFooterMessage(resolvedState),
    recordsErrorMessage,
  };

  const clinic = {
    email: authState.user?.email ?? state.auth.email,
  };

  const auth = {
    email: state.auth.email,
    emailError: state.auth.emailError,
    isSubmitting: state.auth.isSubmitting,
    password: state.auth.password,
    passwordError: state.auth.passwordError,
    submitError: state.auth.submitError,
  };

  const authGate = {
    isLoading: allowUnauthenticated ? false : authState.loadingAuthState,
    retry: authState.manualUpdateTrigger,
  };

  const home = {
    patientCount: patientSummaries.length,
    recentPatients,
    recordCount: sortedRecords.length,
    todayRecordCount,
  };

  const settings = {
    language: state.settings.language,
    promptVersion: state.settings.promptVersion,
  };

  const patient = {
    current: currentPatient,
    draft: {
      chartNumber: state.patientDraft.chartNumber,
      chartNumberError: state.patientDraft.chartNumberError,
      name: state.patientDraft.name,
      nameError: state.patientDraft.nameError,
    },
  };

  const camera = {
    activeFacingMode,
    canSwitch: canSwitchCamera,
    frameSize: state.camera.frameSize,
    message: state.camera.message,
    status: state.camera.status,
  };

  const capture = {
    image: state.capture.image,
    isDisabled: selectIsCaptureDisabled(resolvedState),
  };

  const simulation = {
    hasComparisonResult: selectHasComparisonResult(resolvedState),
    isProcessing: selectIsSimulationProcessing(resolvedState),
    message: state.simulation.message,
    progress: state.simulation.progress,
    progressLabel: getSmileSimulationProgressLabel(
      resolvedState.simulation.progress,
      copy.progress,
    ),
    progressOffset: selectSimulationProgressOffset(resolvedState),
    resultImage: state.simulation.resultImage,
    resultRecordId: state.simulation.resultRecordId,
    status: state.simulation.status,
  };

  const history = {
    hasRecords: sortedRecords.length > 0,
    patients: filteredPatients,
    query: state.history.query,
    selectedPatient: selectedPatientSummary,
    selectedRecord: selectedRecordDetail,
  };

  const download = {
    hasClinicWatermark: downloadPreview?.hasClinicWatermark ?? false,
    includeClinicWatermark: downloadPreview?.includeClinicWatermark ?? true,
    includeServiceWatermark: downloadPreview?.includeServiceWatermark ?? true,
    isDownloadingImage,
    isPreparingPreview: downloadPreview?.isPreparingPreview ?? false,
    isPreviewOpen: downloadPreview != null,
    isUploadingClinicWatermark:
      downloadPreview?.isUploadingClinicWatermark ?? false,
    previewErrorMessage: downloadPreview?.errorMessage ?? null,
    previewImage: downloadPreview?.previewImageUrl ?? null,
  };

  const comparison = {
    bindings,
    containerRef: comparisonContainerRef,
    isDragging: state.comparison.isDragging,
    value: state.comparison.value,
  };

  const media = {
    cameraFrameViewportRef,
    captureCanvasRef,
    startCameraStream,
    stopCameraStream,
    videoRef,
  };

  const actions = {
    capture: handleCapture,
    closeDownloadPreview,
    confirmDownloadPreview: handleConfirmDownloadPreview,
    downloadCurrentResultImage: handleDownloadCurrentResultImage,
    downloadSelectedRecordImage: handleDownloadSelectedRecordImage,
    editCurrentPatientInfo: handleEditCurrentPatientInfo,
    goBackToHistoryList: handleBackToHistoryList,
    goBackToPatientDetail: handleBackToPatientDetail,
    goHome: handleGoHome,
    openCurrentPatientDetail: handleOpenCurrentPatientDetail,
    openHistory: handleOpenHistory,
    openPatientDetail: handleOpenPatientDetail,
    openRecordDetail: handleOpenRecordDetail,
    logout: handleLogout,
    retake: handleRetake,
    retry: handleRetrySimulation,
    startNewSimulation: handleStartNewSimulation,
    startSimulation: handleStartSimulation,
    switchCamera,
    submitPatientInfo: handleSubmitPatientInfo,
    submitLogin: handleSubmitLogin,
    toggleDownloadPreviewClinicWatermark: (isEnabled: boolean) =>
      handleSetDownloadPreviewWatermark("clinic", isEnabled),
    toggleDownloadPreviewServiceWatermark: (isEnabled: boolean) =>
      handleSetDownloadPreviewWatermark("service", isEnabled),
    uploadDownloadPreviewClinicWatermark: handleUploadClinicWatermark,
    uploadCaptureImage: handleUploadCaptureImage,
    updateEmail,
    updateHistoryQuery,
    updatePatientChartNumber,
    updatePatientName,
    updatePassword,
    updateSimulationLanguage,
    updateSimulationPromptVersion,
  };

  return {
    actions,
    auth,
    authGate,
    camera,
    clinic,
    capture,
    comparison,
    download,
    history,
    home,
    media,
    patient,
    settings,
    step: resolvedStep,
    simulation,
    view,
  };
}

export type SmileSimulationController = ReturnType<
  typeof useSmileSimulationController
>;
